/**
 * ID utilities for handling ID type conversions
 * This file provides helper functions to handle the transition from string UUIDs to numeric IDs
 *
 * NOTE: This is the canonical location for ID conversion utilities in the application.
 * The duplicate functions previously in utils.ts have been removed to avoid confusion.
 */

/**
 * Safely converts a value to number
 * Handles both string and number inputs
 * @param value The value to convert
 * @returns A number
 */
export function toNumber(value: string | number | undefined | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  return parseInt(value, 10) || 0;
}

/**
 * Safely converts a value to string
 * Handles both string and number inputs
 * @param value The value to convert
 * @returns A string
 */
export function toString(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

/**
 * Compare two ID values safely (either string or number)
 * @param id1 First ID to compare
 * @param id2 Second ID to compare
 * @returns boolean indicating if they match
 */
export function compareIds(id1: string | number | undefined | null, id2: string | number | undefined | null): boolean {
  if (id1 === undefined || id1 === null || id2 === undefined || id2 === null) {
    return false;
  }
  
  // Convert both to string for comparison
  return String(id1) === String(id2);
}

/**
 * Check if a string is a valid UUID
 * @param id String to check
 * @returns boolean indicating if it's a valid UUID
 */
export function isUuid(id: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
}

/**
 * Convert a numeric ID to a UUID format for API calls that require UUID
 * This is used when backend expects UUID but frontend has numeric ID
 * @param id The ID (string or number) to potentially convert
 * @returns A UUID formatted string
 */
export function toUuidFormat(id: string | number | undefined | null): string {
  // If already a UUID, return as-is
  const strId = toString(id);
  if (isUuid(strId)) return strId;
  
  // For numeric IDs, create a deterministic UUID-like format
  // This pads the number with zeros and formats it as UUID
  // For example: 47 -> "00000000-0000-0000-0000-000000000047"
  if (strId) {
    const paddedId = strId.padStart(12, '0');
    return `00000000-0000-0000-0000-${paddedId}`;
  }
  
  return '';
}